import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { useAuth } from "../AuthContext";
import { AREA_META } from "../constants/areaMeta";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { AiDisclaimer } from "../components/AiDisclaimer";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import { RiseLogo } from "../components/RiseLogo";
import { APP_URL } from "../constants/brand";

const PILLARS = Object.keys(AREA_META);
const hexToRgb = (h) => {
  const n = (h || "#3F6E85").replace("#", "");
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
};

export default function Summary() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [longevity, setLongevity] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [qrs, setQrs] = useState(null);
  const contentRef = useRef(null);
  const shareRef = useRef(null);
  const infoRef = useRef(null);

  useEffect(() => {
    api.get("/program").then((r) => setProgram(r.data)).catch(() => {});
    api.get("/longevity").then((r) => setLongevity(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const links = [
      { key: "breath", url: `${APP_URL}/learn/breathing` },
      { key: "program", url: `${APP_URL}/program` },
      { key: "diary", url: `${APP_URL}/diary` },
    ];
    Promise.all(links.map((l) =>
      QRCode.toDataURL(l.url, { margin: 1, width: 220, color: { dark: "#2B4A59", light: "#FFFFFF" } })
        .then((img) => ({ ...l, img }))
    )).then(setQrs).catch(() => {});
  }, []);

  const hasProgram = program && program.weeks && program.weeks.length > 0;

  const chartData = hasProgram
    ? program.weeks.map((w, i) => {
        const row = { week: `S${i + 1}` };
        PILLARS.forEach((p) => {
          let done = 0, total = 0;
          w.days.forEach((d) => d.tasks.forEach((tk) => {
            if (tk.pillar === p) { total += 1; if (tk.done) done += 1; }
          }));
          row[p] = total ? Math.round((done / total) * 100) : 0;
        });
        return row;
      })
    : [];

  const overall = hasProgram
    ? (() => {
        let done = 0, total = 0;
        program.weeks.forEach((w) => w.days.forEach((d) => d.tasks.forEach((tk) => { total += 1; if (tk.done) done += 1; })));
        return total ? Math.round((done / total) * 100) : 0;
      })()
    : 0;

  const downloadPdf = async () => {
    if (!infoRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(infoRef.current, { scale: 2, backgroundColor: "#FDFBF7", useCORS: true });
      const img = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 22;
      const ratio = Math.min((pw - margin * 2) / canvas.width, (ph - margin * 2) / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(img, "JPEG", (pw - w) / 2, margin, w, h);
      pdf.save("RISE-infografica.pdf");
    } catch (e) {
      toast.error(lang === "it" ? "Errore nel PDF" : "PDF error");
    } finally {
      setDownloading(false);
    }
  };

  const shareJourney = async () => {
    if (!shareRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(shareRef.current, { scale: 2, backgroundColor: "#2B4A59" });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "rise-viaggio.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: t("share_card_title") });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "rise-viaggio.png"; a.click();
        URL.revokeObjectURL(url);
        toast.success(t("share_ready"));
      }
    } catch (e) {
      if (e && e.name === "AbortError") { /* user cancelled share */ }
      else toast.error(lang === "it" ? "Errore" : "Error");
    } finally {
      setSharing(false);
    }
  };

  const pillarAvg = (p) => chartData.length ? Math.round(chartData.reduce((a, r) => a + r[p], 0) / chartData.length) : 0;
  const bioDelta = longevity && longevity.bio ? longevity.bio.delta : 0;

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6E6B65] mb-4 hover:text-[#3F6E85]" data-testid="summary-back">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
        <div className="flex items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{t("summary_title")}</h1>
            <p className="text-[#6E6B65]">{t("summary_sub")}</p>
          </div>
          {hasProgram && (
            <div className="flex gap-2">
              <Button data-testid="share-journey-btn" onClick={shareJourney} disabled={sharing} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white gap-2">
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} {t("share_journey")}
              </Button>
              <Button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={downloading} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white gap-2">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("infographic_pdf")}
              </Button>
            </div>
          )}
        </div>

        {!hasProgram ? (
          <div className="rounded-2xl p-10 bg-[#F3F1ED] text-center text-[#6E6B65]" data-testid="summary-empty">
            {lang === "it" ? "Genera prima il tuo programma di 12 settimane." : "Generate your 12-week program first."}
          </div>
        ) : (
          <div ref={contentRef} className="bg-[#FDFBF7] p-2" data-testid="summary-content">
            <div className="rounded-3xl p-8 bg-[#2B4A59] text-[#FDFBF7] mb-6">
              <p className="text-xs uppercase tracking-widest text-white/50 mb-1">{t("overall_completion")}</p>
              <p className="font-serif text-6xl">{overall}%</p>
            </div>

            <div className="rounded-3xl p-6 bg-white border border-stone-200 mb-6" data-testid="pillar-bars">
              <h2 className="font-serif text-2xl tracking-tight mb-6">{t("progress_by_pillar")}</h2>
              <div className="space-y-5">
                {PILLARS.map((p) => {
                  const Icon = AREA_META[p].icon;
                  const avg = pillarAvg(p);
                  return (
                    <div key={p} data-testid={`pillar-bar-${p}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-sm font-medium text-[#2B4A59]">
                          <Icon className="w-4 h-4" style={{ color: AREA_META[p].color }} /> {t(p)}
                        </span>
                        <span className="text-sm font-bold" style={{ color: AREA_META[p].color }}>{avg}%</span>
                      </div>
                      <div className="h-3.5 rounded-full bg-[#F3F1ED] overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${avg}%`, backgroundColor: AREA_META[p].color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {hasProgram && <AiDisclaimer />}

        {/* Off-screen branded share card */}
        {hasProgram && (
          <div style={{ position: "fixed", left: "-99999px", top: 0 }} aria-hidden="true">
            <div ref={shareRef} data-testid="share-card" style={{ width: 620 }} className="relative overflow-hidden bg-[#2B4A59] text-[#FDFBF7] p-10">
              <div className="grain opacity-40" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-8">
                  <RiseLogo width={44} height={29} />
                  <span className="font-serif text-2xl font-semibold tracking-tight">RISE your LIFE</span>
                </div>
                <p className="text-xs uppercase tracking-widest text-[#B5651D] font-semibold mb-1">{t("share_card_title")}</p>
                <p className="font-serif text-3xl mb-6">{user?.name}</p>

                <div className="flex items-end gap-6 mb-8">
                  <div>
                    <p className="font-serif text-7xl leading-none text-[#B5651D]">{overall}%</p>
                    <p className="text-sm text-white/60 mt-1">12 {t("share_weeks")} · {t("share_completion")}</p>
                  </div>
                  {bioDelta > 0 && (
                    <div className="ml-auto text-right">
                      <p className="font-serif text-5xl leading-none text-[#8FD9A8]">−{bioDelta}</p>
                      <p className="text-sm text-white/60 mt-1">{t("share_bio_younger")}</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {PILLARS.map((p) => {
                    const Icon = AREA_META[p].icon;
                    return (
                      <div key={p} className="rounded-2xl p-4 bg-white/5 border border-white/10">
                        <Icon className="w-5 h-5 mb-2" style={{ color: AREA_META[p].color }} />
                        <p className="font-serif text-2xl">{pillarAvg(p)}%</p>
                        <p className="text-[11px] text-white/60">{t(p)}</p>
                      </div>
                    );
                  })}
                </div>

                <p className="text-sm text-white/50 mt-8">{t("tagline")}</p>
              </div>
            </div>
          </div>
        )}

        {/* Hidden one-page INFOGRAPHIC used for the PDF (rendered via html2canvas) */}
        <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden>
          <div ref={infoRef} data-testid="pdf-infographic" style={{ width: 794, background: "#FDFBF7", padding: 44, fontFamily: "Georgia, serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
              <RiseLogo width={64} height={42} />
              <div>
                <div style={{ fontSize: 30, fontWeight: 700, color: "#3F6E85", letterSpacing: "-0.5px" }}>RISE your LIFE</div>
                <div style={{ fontSize: 14, color: "#6E6B65" }}>{t("summary_title")} · {user?.name || ""}</div>
              </div>
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div style={{ fontSize: 13, color: "#6E6B65", textTransform: "uppercase", letterSpacing: 1 }}>{t("overall_completion")}</div>
                <div style={{ fontSize: 52, fontWeight: 700, color: "#3F6E85", lineHeight: 1 }}>{overall}%</div>
                {bioDelta > 0 && <div style={{ fontSize: 15, color: "#3B604E", fontWeight: 700 }}>−{bioDelta} {t("share_bio_younger")}</div>}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, marginBottom: 26 }}>
              {[
                { key: "nutrition", color: "#3F6E85", label: lang === "it" ? "Alimentazione" : "Nutrition" },
                { key: "exercise", color: "#E08A3C", label: lang === "it" ? "Movimento" : "Movement" },
                { key: "breathing", color: "#C81D6B", label: lang === "it" ? "Respiro" : "Breathing" },
              ].map((b) => {
                const avg = pillarAvg(b.key);
                return (
                  <div key={b.key} style={{ background: b.color, borderRadius: 24, padding: 24, color: "#fff", minHeight: 190 }}>
                    <div style={{ fontSize: 15, opacity: 0.9, textTransform: "uppercase", letterSpacing: 1, fontFamily: "Arial, sans-serif" }}>{b.label}</div>
                    <div style={{ fontSize: 58, fontWeight: 700, lineHeight: 1.1, margin: "10px 0" }}>{avg}%</div>
                    <div style={{ height: 12, borderRadius: 8, background: "rgba(255,255,255,0.28)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${avg}%`, background: "#fff", borderRadius: 8 }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 18, marginBottom: 24, fontFamily: "Arial, sans-serif" }}>
              {["sleep", "cold", "relationships"].map((p) => (
                <div key={p} style={{ flex: 1, background: "#fff", border: "1px solid #ece9e3", borderRadius: 18, padding: 16 }}>
                  <div style={{ fontSize: 12, color: "#6E6B65", textTransform: "uppercase", letterSpacing: 1 }}>{t(p)}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: AREA_META[p].color }}>{pillarAvg(p)}%</div>
                </div>
              ))}
            </div>

            {qrs && (
              <div style={{ marginBottom: 18, fontFamily: "Arial, sans-serif" }}>
                <div style={{ fontSize: 12, color: "#6E6B65", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{t("qr_scan_hint")}</div>
                <div style={{ display: "flex", gap: 14 }}>
                  {qrs.map((q) => (
                    <div key={q.key} style={{ flex: 1, background: "#fff", border: "1px solid #ece9e3", borderRadius: 16, padding: 12, textAlign: "center" }}>
                      <img src={q.img} alt={q.key} style={{ width: 96, height: 96 }} />
                      <div style={{ fontSize: 12, color: "#3F6E85", fontWeight: 700, marginTop: 6 }}>{t(`qr_${q.key}`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ fontSize: 15, fontStyle: "italic", color: "#6E6B65", borderTop: "2px solid #3F6E85", paddingTop: 14 }}>{t("tagline")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}